import React, { useEffect, useState, useCallback, useRef } from "react";
import HeartLoader from "@/components/HeartLoader";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  GraduationCap, Loader2, Sparkles, Wand2, Clock, Flame, Search as SearchIcon, ChevronDown,
  Award, Share2, Download, Copy, CheckCircle2, XCircle, ArrowLeft, BookOpen, Users, Trophy, Coins, Star, Medal, FileText,
  Landmark, MapPin, Mail, MessageSquare, Send, Link as LinkIcon,
  Video, Plus, Trash2, Lock, ExternalLink, Calendar, Radio, PlayCircle, Heart,
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api, API, apiError } from "@/lib/api";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import StudyGames from "@/components/StudyGames";
import ShareKindCard from "@/components/ShareKindCard";

export default function Learn() {
  const { user, setUser, refreshUser, countryObj, country } = useApp();
  const [params, setParams] = useSearchParams();
  const courseId = params.get("course");
  const verifyCode = params.get("verify");
  const refCode = params.get("ref") || "";
  const [tab, setTab] = useState("catalog");
  const isIndia = (country || "").toUpperCase() === "IN" || /india|bharat/i.test(countryObj?.name || "");
  useEffect(() => { if (tab === "exams" && !isIndia) setTab("catalog"); }, [isIndia, tab]);

  if (verifyCode) return <VerifyView code={verifyCode} onBack={() => setParams({})} />;
  if (courseId) return <CourseView courseId={courseId} refCode={refCode} user={user} setUser={setUser} refreshUser={refreshUser} onBack={() => setParams({})} />;

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-24 pb-16" data-testid="learn-page">
      <Seo
        title="Courses — Yoga, Advaita, AI, Digital Transformation & Wellbeing"
        description="Learn and teach on My Kind's VishwaGuru-University: courses on Yoga, Advaita, AI, Digital Transformation, wellbeing and more. Study daily modules, pass the exam, earn a shareable certificate and credits."
        keywords="online courses, yoga course, Advaita course, AI course, digital transformation course, wellbeing, meditation, learn and earn, free certificate, distance learning"
        path="/learn"
        jsonLd={{ "@context": "https://schema.org", "@type": "EducationalOrganization", "name": "VishwaGuru-University by My Kind", "url": "https://mykind.app/learn", "description": "Decentralised distance-learning: create and take courses on Yoga, Advaita, AI, Digital Transformation and wellbeing." }}
      />
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-2"><GraduationCap size={15} /> VishwaGuru-University · Teacher of The World</div>
      <h1 className="font-serif text-4xl sm:text-5xl tracking-tighter">Create. Take. Share a course. Earn.</h1>
      <p className="text-sec mt-3 max-w-2xl">A decentralised, democratic distance-learning institution. Build a course on any topic, study daily modules with quizzes &amp; assignments, pass the final exam, and earn a shareable VishwaGuru-University certificate — plus credits when others learn from your link.</p>

      <div className="flex flex-wrap gap-1 p-1 rounded-2xl bg-elevated border border-rud w-full sm:w-fit mt-6">
        {[["catalog", "Explore"], ...(isIndia ? [["exams", "Competitive Exams"]] : []), ["games", "Study Games"], ["create", "Create a course"], ["mine", "My learning"], ["progress", "My Progress"], ["leaderboard", "Leaderboard"]].map(([k, l]) => (
          <button key={k} data-testid={`learn-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-1.5 rounded-full text-sm transition-colors ${tab === k ? "bg-brand text-black" : "text-sec"}`}>{l}</button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "catalog" && <Catalog onOpen={(id) => setParams({ course: id })} />}
        {tab === "exams" && isIndia && <CompetitiveExams user={user} setUser={setUser} countryObj={countryObj} country={country} onOpen={(id) => setParams({ course: id })} />}
        {tab === "games" && <StudyGames category="Indian Competitive Exams" />}
        {tab === "create" && <CreateCourse user={user} setUser={setUser} onCreated={(id) => setParams({ course: id })} />}
        {tab === "mine" && <MyLearning user={user} onOpen={(id) => setParams({ course: id })} onVerify={(code) => setParams({ verify: code })} />}
        {tab === "progress" && <ProgressDashboard user={user} onOpen={(id) => setParams({ course: id })} />}
        {tab === "leaderboard" && <Leaderboard />}
      </div>
    </div>
  );
}

function ProgressDashboard({ user, onOpen }) {
  const [data, setData] = useState(null);
  useEffect(() => { if (user) api.get("/lms/progress").then(({ data }) => setData(data)).catch(() => {}); }, [user]);
  if (user === null) return <p className="text-sec text-sm" data-testid="progress-signin">Sign in to see your learning progress.</p>;
  if (!data) return <p className="text-sec text-sm">Loading…</p>;
  const s = data.summary;
  return (
    <div data-testid="progress-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[["Courses", s.courses_enrolled], ["Passed", s.courses_passed], ["Certificates", s.certificates], ["Modules done", s.modules_completed], ["Avg grade", s.avg_final_grade != null ? s.avg_final_grade + "%" : "—"]].map(([l, v], i) => (
          <div key={i} className="rud-card rounded-2xl p-4"><div className="font-serif text-3xl">{v}</div><div className="text-xs text-sec">{l}</div></div>
        ))}
      </div>

      {s.courses_enrolled > 0 && (
        <ShareKindCard title="I'm learning on VishwaGuru-University 🎓"
          subtitle={`${s.modules_completed} modules done · ${s.certificates} certificate${s.certificates === 1 ? "" : "s"}${s.avg_final_grade != null ? ` · avg ${s.avg_final_grade}%` : ""}`} />
      )}

      <div className="grid md:grid-cols-2 gap-4 mt-5">
        <div className="rud-card rounded-2xl p-5" data-testid="areas-to-improve">
          <div className="text-sm font-medium text-brand mb-2">Areas to improve</div>
          {data.areas_to_improve.length === 0 ? <p className="text-sec text-sm">No weak spots detected yet — take a few module quizzes to get insights.</p> : (
            <div className="space-y-2">{data.areas_to_improve.map((a, i) => (
              <div key={i} className="flex items-center justify-between gap-2 text-sm"><span className="truncate">{a.area}</span>
                <span className="shrink-0 text-xs px-2 py-0.5 rounded-full border border-red-500/50 text-red-400">{a.score}%</span></div>
            ))}</div>
          )}
        </div>
        <div className="rud-card rounded-2xl p-5" data-testid="strengths">
          <div className="text-sm font-medium text-brand mb-2">Your strengths</div>
          {data.strengths.length === 0 ? <p className="text-sec text-sm">Score 80%+ on module quizzes to build your strengths list.</p> : (
            <div className="space-y-2">{data.strengths.map((a, i) => (
              <div key={i} className="flex items-center justify-between gap-2 text-sm"><span className="truncate">{a.area}</span>
                <span className="shrink-0 text-xs px-2 py-0.5 rounded-full border border-green-500/50 text-green-400">{a.score}%</span></div>
            ))}</div>
          )}
        </div>
      </div>

      <h3 className="font-serif text-2xl mt-8 mb-3">Course progress</h3>
      {data.courses.length === 0 ? <p className="text-sec text-sm">You haven't enrolled in any course yet.</p> : (
        <div className="space-y-3" data-testid="progress-courses">
          {data.courses.map((c) => (
            <button key={c.course_id} onClick={() => onOpen?.(c.course_id)} className="w-full text-left rud-card rounded-xl p-4 hover:border-brand/50 transition-colors">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0"><div className="text-sm truncate">{c.title}</div><div className="text-[11px] text-sec">{c.exam_tag || c.category}{c.passed ? " · Passed ✓" : ""}{c.certificate ? " · Certified 🎓" : ""}</div></div>
                <div className="text-xs text-sec shrink-0">{c.modules_done}/{c.modules_total} · {c.exam_final != null ? c.exam_final + "%" : ""}</div>
              </div>
              <div className="h-2 rounded-full bg-elevated border border-rud overflow-hidden mt-2"><div className="h-full bg-brand" style={{ width: `${c.progress_pct}%` }} /></div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


function Stars({ value, count }) {
  return (
    <span className="flex items-center gap-1 text-amber-400 text-xs">
      <Star size={12} className="fill-amber-400" /> {value ? value.toFixed(1) : "New"}{count ? <span className="text-sec">({count})</span> : null}
    </span>
  );
}

function Catalog({ onOpen }) {
  const { country } = useApp();
  const [cats, setCats] = useState(["All"]);
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");
  const [courses, setCourses] = useState([]);
  const [recos, setRecos] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/lms/catalog", { params: { category: cat, q: q || undefined, country } });
      setCourses(data.courses || []);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [cat, q, country]);

  useEffect(() => { api.get("/lms/categories").then(({ data }) => setCats(["All", ...data.categories])).catch(() => {}); }, []);
  useEffect(() => { api.get("/lms/recommended", { params: { country } }).then(({ data }) => setRecos(data.courses || [])).catch(() => {}); }, [country]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const trending = courses.filter((c) => c.trending);
  return (
    <div>
      <div className="relative mb-4 max-w-md">
        <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-sec" />
        <input data-testid="learn-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search 100+ courses…" className="rud-input w-full rounded-full pl-10 pr-4 py-2.5 text-sm" />
      </div>
      <div className="flex flex-wrap gap-2 mb-6">
        {cats.map((c) => (
          <button key={c} data-testid={`learn-cat-${c.replace(/[^a-z]+/gi, "-").toLowerCase()}`} onClick={() => setCat(c)}
            className={`text-xs rounded-full px-3.5 py-1.5 border transition-colors ${cat === c ? "bg-brand text-black border-brand" : "border-rud text-sec hover:border-brand"}`}>{c}</button>
        ))}
      </div>

      {loading ? <div className="grid place-items-center py-20 text-sec"><HeartLoader /></div> : (
        <>
          {cat === "All" && !q && recos.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center gap-2 text-sm font-medium mb-3"><Sparkles size={16} className="text-brand" /> Recommended for you <span className="text-xs text-sec font-normal">· matched to your skills &amp; industry demand</span></div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="learn-recommended">{recos.slice(0, 6).map((c) => <CourseCard key={c.id} c={c} onOpen={onOpen} />)}</div>
            </div>
          )}
          {cat === "All" && !q && trending.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center gap-2 text-sm font-medium mb-3"><Flame size={16} className="text-orange-400" /> Trending now</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{trending.slice(0, 6).map((c) => <CourseCard key={c.id} c={c} onOpen={onOpen} />)}</div>
            </div>
          )}
          <div className="text-sm text-sec mb-3">{courses.length} courses{cat !== "All" ? ` in ${cat}` : ""}</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="learn-catalog-grid">
            {courses.map((c) => <CourseCard key={c.id} c={c} onOpen={onOpen} />)}
          </div>
          {courses.length === 0 && <div className="text-center text-sec py-16">No courses found. Try creating one!</div>}
        </>
      )}
    </div>
  );
}

function CourseCard({ c, onOpen }) {
  const lvl = { Beginner: "text-emerald-400", Intermediate: "text-amber-400", Advanced: "text-rose-400" }[c.level] || "text-sec";
  return (
    <motion.button whileHover={{ y: -4 }} data-testid={`course-card-${c.id}`} onClick={() => onOpen(c.id)}
      className="rud-card rounded-2xl p-5 text-left flex flex-col h-full">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] uppercase tracking-widest text-brand">{c.category}</span>
        <span className="flex items-center gap-2">
          {c.demand && <span className="text-[10px] flex items-center gap-1 text-emerald-400" title="High industry demand">📈 In demand</span>}
          {c.trending && <span className="text-[10px] flex items-center gap-1 text-orange-400"><Flame size={11} /> Trending</span>}
        </span>
      </div>
      <div className="font-serif text-xl leading-tight">{c.title}</div>
      <p className="text-sm text-sec mt-1.5 line-clamp-2 flex-1">{c.description}</p>
      <div className="flex items-center gap-3 mt-3 text-xs">
        <span className={`font-medium ${lvl}`}>{c.level}</span>
        <Stars value={c.rating_avg} count={c.rating_count} />
      </div>
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-rud text-xs text-sec">
        <span className="flex items-center gap-1"><Clock size={12} /> {c.duration_hours}h · {c.module_count} modules</span>
        <span className="flex items-center gap-1 text-brand font-medium"><Coins size={12} /> {c.price_credits}{c.base_credits > c.price_credits && <span className="text-sec line-through ml-1 text-[10px] opacity-60">{c.base_credits}</span>}</span>
      </div>
    </motion.button>
  );
}

function ShareCourse({ courseId, title, refCode, compact }) {
  const origin = window.location.origin;
  const link = `${origin}/learn?course=${courseId}${refCode ? `&ref=${refCode}` : ""}`;
  const msg = `Join my course "${title}" on VishwaGuru-University: ${link}`;
  const enc = encodeURIComponent(msg);
  const share = (net) => {
    if (net === "copy") { navigator.clipboard.writeText(link).then(() => toast.success("Course link copied")).catch(() => toast.error("Copy failed")); return; }
    const links = {
      whatsapp: `https://wa.me/?text=${enc}`,
      email: `mailto:?subject=${encodeURIComponent(`Join my course: ${title}`)}&body=${enc}`,
      sms: `sms:?&body=${enc}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${enc}`,
    };
    window.open(links[net], net === "email" || net === "sms" ? "_self" : "_blank", "noopener,noreferrer");
  };
  return (
    <div data-testid={`share-course-${courseId}`} className={compact ? "flex flex-wrap gap-1.5" : "flex flex-wrap gap-2"}>
      <button data-testid={`share-course-whatsapp-${courseId}`} onClick={() => share("whatsapp")} className="btn-ghost rounded-full text-xs h-8 px-3 flex items-center gap-1.5"><Share2 size={12} /> WhatsApp</button>
      <button data-testid={`share-course-email-${courseId}`} onClick={() => share("email")} className="btn-ghost rounded-full text-xs h-8 px-3 flex items-center gap-1.5"><Mail size={12} /> Email</button>
      <button data-testid={`share-course-sms-${courseId}`} onClick={() => share("sms")} className="btn-ghost rounded-full text-xs h-8 px-3 flex items-center gap-1.5"><MessageSquare size={12} /> SMS</button>
      <button data-testid={`share-course-copy-${courseId}`} onClick={() => share("copy")} className="btn-ghost rounded-full text-xs h-8 px-3 flex items-center gap-1.5"><LinkIcon size={12} /> Copy link</button>
    </div>
  );
}

function CompetitiveExams({ user, setUser, countryObj, country, onOpen }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState("");
  const [busy, setBusy] = useState("");
  const countryName = countryObj?.name || (country === "IN" ? "Bharat (India)" : (country || "Bharat (India)"));

  const load = useCallback(async (st) => {
    setLoading(true);
    try {
      const { data } = await api.get("/lms/competitive-exams", { params: { country: countryName, state: st || undefined } });
      setData(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [countryName]);

  useEffect(() => { load(state); }, [load]);

  const startPrep = async (ex, scope) => {
    if (!user) { toast.error("Sign in to build a prep course."); return; }
    const cost = Math.max(21, Math.ceil((ex.hours || 80) * 2));
    if (!window.confirm(`Build a full "${ex.name}" preparation course (${ex.hours || 80}h)?\n\nThis will use ${cost} credits.`)) return;
    setBusy(ex.name);
    try {
      const { data: r } = await api.post("/lms/exam-prep", {
        exam_name: ex.name, country: countryName, state: scope === "state" ? state : "",
        level: ex.level || "Intermediate", hours: ex.hours || 80,
      });
      if (setUser && user) setUser({ ...user, credits: r.credits_remaining });
      toast.success(`Prep course built for ${r.price_credits} credits`);
      onOpen(r.course.id);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const [examList, setExamList] = useState(null);
  const [examLoading, setExamLoading] = useState("");
  const [mockExam, setMockExam] = useState(null);
  const openExamCourses = async (ex) => {
    setExamLoading(ex.name);
    try {
      const { data } = await api.get(`/lms/catalog?exam_tag=${encodeURIComponent(ex.name)}&category=Competitive%20Exams`);
      setExamList({ name: ex.name, courses: (data.courses || []).slice(0, 30) });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setExamLoading(""); }
  };

  const ExamCard = ({ ex, scope }) => (
    <div className="rud-card rounded-2xl p-5 flex flex-col h-full" data-testid={`exam-card-${ex.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] uppercase tracking-widest text-brand">{ex.category}</span>
        <span className="text-[10px] text-sec">{ex.level}</span>
      </div>
      <div className="font-serif text-lg leading-tight flex-1">{ex.name}</div>
      <div className="text-[11px] text-sec mt-2 flex items-center gap-1"><Clock size={11} /> {ex.hours || 80}h · <span className="text-brand">Free · Guru Dakshina only</span></div>
      <div className="flex flex-wrap gap-2 mt-3">
        <button data-testid={`view-courses-${ex.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} onClick={() => openExamCourses(ex)} disabled={examLoading === ex.name}
          className="btn-primary h-9 px-4 text-xs rounded-full flex items-center justify-center gap-1.5 disabled:opacity-60">
          {examLoading === ex.name ? <Loader2 size={13} className="animate-spin" /> : <BookOpen size={13} />} View Courses
          <span className="ml-1 rounded-full bg-brand text-black text-[9px] font-bold px-1.5 py-0.5 leading-none">Free · Guru Dakshina</span>
        </button>
        <button data-testid={`build-prep-${ex.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} onClick={() => startPrep(ex, scope)} disabled={busy === ex.name}
          className="btn-ghost rounded-full h-9 px-3 text-xs flex items-center justify-center gap-1.5 disabled:opacity-60">
          {busy === ex.name ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />} Custom
        </button>
        <button data-testid={`mock-${ex.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} onClick={() => setMockExam({ name: ex.name, state: scope === "state" ? state : "" })}
          className="btn-ghost rounded-full h-9 px-3 text-xs flex items-center justify-center gap-1.5 text-brand">
          <Sparkles size={13} /> Free Mock Test
        </button>
      </div>
    </div>
  );

  return (
    <div data-testid="competitive-exams">
      <div className="flex items-center gap-2 mb-2"><Landmark className="text-brand" size={20} /><h2 className="font-serif text-2xl">Competitive Exams · {data?.country || countryName}</h2></div>
      <p className="text-sec text-sm max-w-2xl mb-5">National &amp; state-level competitive exams for your geography. Pick an exam and My Kind builds a full, syllabus-mapped preparation course with daily modules, quizzes, a final exam and a certificate.</p>

      {data?.supported && (
        <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="exam-state-picker">
          <MapPin size={15} className="text-brand" />
          <span className="text-sm text-sec">Your state / UT:</span>
          <select value={state} onChange={(e) => { setState(e.target.value); load(e.target.value); }}
            className="rud-input rounded-lg px-3 py-2 text-sm" data-testid="exam-state-select">
            <option value="">Select a state to see state exams</option>
            {(data.states || []).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      )}

      {loading ? <div className="grid place-items-center py-20"><HeartLoader /></div> : (
        <>
          {!data?.supported && (
            <div className="rud-card rounded-xl p-4 mb-6 text-sm text-sec">Detailed state-wise exams are curated for Bharat (India). Showing common exam tracks for {data?.country || countryName} — you can build a tailored prep course for any of them.</div>
          )}
          {(data?.national || []).length > 0 && (
            <div className="mb-8">
              <div className="flex items-center gap-2 text-sm font-medium mb-3"><Trophy size={16} className="text-brand" /> National exams</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="national-exams-grid">
                {data.national.map((ex) => <ExamCard key={ex.name} ex={ex} scope="national" />)}
              </div>
            </div>
          )}

          {data?.supported && (
            <div>
              <div className="flex items-center gap-2 text-sm font-medium mb-3"><MapPin size={16} className="text-brand" /> State exams {state ? `· ${state}` : ""}</div>
              {!state ? (
                <div className="rud-card rounded-xl p-6 text-center text-sec text-sm" data-testid="exam-no-state">Select your state above to see state-level competitive exams (PSC, Police, TET and more).</div>
              ) : (data?.state_exams || []).length === 0 ? (
                <div className="rud-card rounded-xl p-6 text-center text-sec text-sm">No state exams listed for {state} yet.</div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="state-exams-grid">
                  {data.state_exams.map((ex) => <ExamCard key={ex.name} ex={ex} scope="state" />)}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {examList && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setExamList(null)}>
          <div className="rud-card rounded-2xl p-6 w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="exam-courses-modal">
            <div className="flex items-start justify-between mb-1">
              <div><div className="font-serif text-2xl">{examList.name}</div><div className="text-xs text-brand">Free courses · pay only an optional Guru Dakshina</div></div>
              <button onClick={() => setExamList(null)}><XCircle size={20} className="text-sec" /></button>
            </div>
            <button data-testid="exam-gurudakshina-btn" onClick={() => window.dispatchEvent(new CustomEvent("open-gurudakshina"))}
              className="btn-ghost rounded-full h-9 px-3 mt-1 mb-1 flex items-center gap-1.5 text-sm text-brand hover:bg-brand/10">
              <Heart size={15} /> Offer Guru Dakshina 🙏
            </button>
            <button data-testid="exam-modal-mock-btn" onClick={() => { const n = examList.name; setExamList(null); setMockExam({ name: n, state }); }}
              className="btn-primary rounded-full h-9 px-4 mt-1 mb-1 ml-2 inline-flex items-center gap-1.5 text-sm">
              <Sparkles size={15} /> Take a Free Mock Test
            </button>
            {examList.courses.length === 0 ? <p className="text-sec text-sm mt-3">No courses found for this exam yet.</p> : (
              <div className="space-y-2 mt-4" data-testid="exam-courses-list">
                {examList.courses.map((c) => (
                  <button key={c.id} data-testid={`exam-course-${c.id}`} onClick={() => { setExamList(null); onOpen(c.id); }}
                    className="w-full text-left rud-card rounded-xl p-4 hover:border-brand/50 transition-colors flex items-center justify-between gap-3">
                    <span className="text-sm min-w-0 truncate">{c.title}</span>
                    <span className="text-xs text-brand shrink-0">Free →</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {mockExam && <MockTestRunner exam={mockExam} onClose={() => setMockExam(null)} />}
    </div>
  );
}

function MockTestRunner({ exam, onClose }) {
  const [loading, setLoading] = useState(true);
  const [qs, setQs] = useState([]);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [secs, setSecs] = useState(0);

  useEffect(() => {
    let alive = true;
    api.post("/lms/exam-mock", { exam_name: exam.name, state: exam.state || "" })
      .then(({ data }) => { if (!alive) return; setQs(data.questions || []); setSecs(data.duration_sec || 900); })
      .catch((e) => { toast.error(apiError(e.response?.data?.detail)); onClose(); })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [exam]);

  useEffect(() => {
    if (loading || submitted || secs <= 0) return;
    const t = setInterval(() => setSecs((s) => { if (s <= 1) { clearInterval(t); setSubmitted(true); return 0; } return s - 1; }), 1000);
    return () => clearInterval(t);
  }, [loading, submitted, secs]);

  const score = qs.reduce((n, q) => n + (answers[q.qid] === q.answer_index ? 1 : 0), 0);
  const mm = String(Math.floor(secs / 60)).padStart(2, "0");
  const ss = String(secs % 60).padStart(2, "0");

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="rud-card rounded-2xl p-6 w-full max-w-2xl max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="mock-test-runner">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-brand">Free AI Mock Test</div>
            <div className="font-serif text-xl leading-tight">{exam.name}{exam.state ? ` · ${exam.state}` : ""}</div>
          </div>
          <div className="flex items-center gap-2">
            {!loading && !submitted && <span data-testid="mock-timer" className={`text-sm font-mono px-2 py-1 rounded ${secs < 60 ? "text-red-400" : "text-brand"}`}><Clock size={12} className="inline mr-1" />{mm}:{ss}</span>}
            <button onClick={onClose}><XCircle size={20} className="text-sec" /></button>
          </div>
        </div>

        {loading ? (
          <div className="grid place-items-center py-16 text-center">
            <HeartLoader />
            <p className="text-sm text-sec">Generating a fresh previous-year-style paper…</p>
          </div>
        ) : submitted ? (
          <div data-testid="mock-result">
            <div className="rud-card rounded-xl p-5 text-center mb-4">
              <div className="text-3xl font-serif text-brand">{score} / {qs.length}</div>
              <div className="text-sm text-sec mt-1">{score >= qs.length * 0.6 ? "Great going! 🎉" : "Keep practising — you've got this 🌿"}</div>
            </div>
            <div className="space-y-3">
              {qs.map((q, i) => {
                const mine = answers[q.qid];
                const ok = mine === q.answer_index;
                return (
                  <div key={q.qid} className="rud-card rounded-xl p-3">
                    <div className="text-sm font-medium mb-2">{i + 1}. {q.q}</div>
                    {q.options.map((o, oi) => (
                      <div key={oi} className={`text-sm px-2 py-1 rounded ${oi === q.answer_index ? "text-green-400" : oi === mine && !ok ? "text-red-400 line-through" : "text-sec"}`}>
                        {oi === q.answer_index ? "✓ " : oi === mine && !ok ? "✗ " : ""}{o}
                      </div>
                    ))}
                    {q.explanation && <div className="text-[11px] text-sec mt-1 italic">{q.explanation}</div>}
                  </div>
                );
              })}
            </div>
            <button data-testid="mock-close" onClick={onClose} className="btn-primary w-full h-11 rounded-full mt-4 text-sm">Done</button>
          </div>
        ) : (
          <div data-testid="mock-questions">
            <p className="text-xs text-sec mb-4">{qs.length} questions · answer them all, then submit. Free practice 🌿</p>
            <div className="space-y-4">
              {qs.map((q, i) => (
                <div key={q.qid} className="rud-card rounded-xl p-3" data-testid={`mock-q-${i}`}>
                  <div className="text-sm font-medium mb-2">{i + 1}. {q.q}</div>
                  <div className="space-y-1.5">
                    {q.options.map((o, oi) => (
                      <button key={oi} data-testid={`mock-q-${i}-opt-${oi}`} onClick={() => setAnswers((a) => ({ ...a, [q.qid]: oi }))}
                        className={`w-full text-left text-sm px-3 py-2 rounded-lg border transition-colors ${answers[q.qid] === oi ? "border-brand bg-brand/10 text-brand" : "border-rud text-sec hover:border-brand/50"}`}>
                        {String.fromCharCode(65 + oi)}. {o}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <button data-testid="mock-submit" onClick={() => setSubmitted(true)}
              className="btn-primary w-full h-11 rounded-full mt-5 text-sm flex items-center justify-center gap-2">
              <CheckCircle2 size={16} /> Submit ({Object.keys(answers).length}/{qs.length} answered)
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function CreateCourse({ user, setUser, onCreated }) {
  const [topic, setTopic] = useState("");
  const [books, setBooks] = useState("");
  const [hours, setHours] = useState(40);
  const [level, setLevel] = useState("Beginner");
  const [busy, setBusy] = useState(false);
  const create = async () => {
    if (!user) { toast.error("Sign in to create a course."); return; }
    if (topic.trim().length < 2 && books.trim().length < 2) { toast.error("Enter a topic or a book name."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/lms/create", { topic: topic.trim() || `Course on ${books}`, books, duration_hours: Number(hours), level });
      if (setUser && user) setUser({ ...user, credits: data.credits_remaining });
      toast.success(`Course created for ${data.price_credits} credits`);
      onCreated(data.course.id);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="max-w-xl rud-card rounded-2xl p-6" data-testid="learn-create-panel">
      <div className="flex items-center gap-2 mb-2"><Wand2 className="text-brand" /><h3 className="font-serif text-2xl">Create your course</h3></div>
      <p className="text-sec text-sm mb-5">My Kind builds a full syllabus of daily modules (reading + quiz + assignment) and a final exam. Fee scales with length (~2 credits/hour).</p>
      <label className="text-xs uppercase tracking-widest text-sec block mb-1.5">What do you want to learn?</label>
      <textarea data-testid="create-topic" value={topic} onChange={(e) => setTopic(e.target.value)} rows={2}
        placeholder="e.g. Options trading, Sanskrit, React, Ayurveda, Astrophysics…" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm resize-none" />
      <label className="text-xs uppercase tracking-widest text-sec block mt-3 mb-1.5">Or base it on book(s) <span className="text-sec normal-case">(optional)</span></label>
      <input data-testid="create-books" value={books} onChange={(e) => setBooks(e.target.value)}
        placeholder="e.g. Atomic Habits; Thinking, Fast and Slow" className="rud-input w-full rounded-lg px-3 py-2.5 text-sm" />
      <p className="text-[11px] text-sec mt-1">My Kind teaches the ideas from these books in original words — a course built around them.</p>
      <div className="grid grid-cols-2 gap-3 mt-4">
        <div>
          <label className="text-xs uppercase tracking-widest text-sec block mb-1.5">Duration (hours)</label>
          <select data-testid="create-hours" value={hours} onChange={(e) => setHours(e.target.value)} className="rud-input w-full rounded-lg px-3 py-2.5 text-sm">
            {[21, 30, 40, 60, 80, 100, 150, 200, 300, 500].map((h) => <option key={h} value={h}>{h} hours</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-sec block mb-1.5">Level</label>
          <select data-testid="create-level" value={level} onChange={(e) => setLevel(e.target.value)} className="rud-input w-full rounded-lg px-3 py-2.5 text-sm">
            {["Beginner", "Intermediate", "Advanced"].map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
      </div>
      <button data-testid="create-submit" onClick={create} disabled={busy} className="btn-primary w-full h-11 mt-5 flex items-center justify-center gap-2 disabled:opacity-60">
        {busy ? <><Loader2 size={16} className="animate-spin" /> Building your syllabus…</> : <><Sparkles size={16} /> Create course</>}
      </button>
    </div>
  );
}

function MockTest({ courseId, examTag, user, setUser }) {
  const [phase, setPhase] = useState("idle"); // idle | running | done
  const [test, setTest] = useState(null);
  const [answers, setAnswers] = useState({});
  const [left, setLeft] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const startedRef = React.useRef(0);

  const loadHistory = useCallback(() => {
    api.get(`/lms/mock-test/${courseId}/history`).then(({ data }) => setHistory(data.attempts || [])).catch(() => {});
  }, [courseId]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const submit = useCallback(async (autoTest) => {
    const tst = autoTest || test;
    if (!tst || busy) return;
    setBusy(true);
    try {
      const taken = Math.round((Date.now() - startedRef.current) / 1000);
      const { data } = await api.post(`/lms/mock-test/${tst.test_id}/submit`, { answers, time_taken_sec: taken });
      setResult(data); setPhase("done"); loadHistory();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }, [test, answers, busy, loadHistory]);

  useEffect(() => {
    if (phase !== "running") return;
    if (left <= 0) { submit(); return; }
    const t = setTimeout(() => setLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [phase, left, submit]);

  const start = async () => {
    if (!user) { toast.error("Sign in to take a mock test."); return; }
    if (!window.confirm("Start a timed mock test? This uses 5 credits.")) return;
    setBusy(true); setResult(null); setAnswers({});
    try {
      const { data } = await api.post(`/lms/mock-test/${courseId}/start`, {});
      setTest(data); setLeft(data.duration_sec); startedRef.current = Date.now(); setPhase("running");
      if (setUser && user) setUser({ ...user, credits: data.credits_remaining });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  const best = history.length ? Math.max(...history.map((h) => h.score_pct || 0)) : null;

  return (
    <div className="rud-card rounded-2xl p-5 mt-6 border border-brand/30" data-testid="mock-test">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand"><Landmark size={14} /> Exam Practice · Timed Mock Test</div>

      {phase === "idle" && (
        <div className="mt-2">
          <p className="text-sm text-sec">Sit a timed, previous-year-style mock test for <b className="text-[var(--rud-text)]">{examTag}</b> — 10 questions, 12 minutes, instant scoring with explanations. <span className="text-brand">5 credits per attempt.</span></p>
          <div className="flex flex-wrap items-center gap-3 mt-3">
            <button data-testid="mock-start-btn" onClick={start} disabled={busy} className="btn-primary h-10 px-5 rounded-full text-sm flex items-center gap-2 disabled:opacity-60">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Wand2 size={15} />} Start mock test
            </button>
            {best != null && <span className="text-sm text-sec">Best: <b className="text-brand">{best}%</b> · {history.length} attempts</span>}
          </div>
        </div>
      )}

      {phase === "running" && test && (
        <div className="mt-3" data-testid="mock-running">
          <div className="flex items-center justify-between mb-3 sticky top-0">
            <span className="text-sm text-sec">{Object.keys(answers).length}/{test.count} answered</span>
            <span className={`font-mono text-lg flex items-center gap-1.5 ${left < 60 ? "text-red-400" : "text-brand"}`} data-testid="mock-timer"><Clock size={16} /> {mmss(left)}</span>
          </div>
          <div className="space-y-4 max-h-[60vh] overflow-auto pr-1">
            {test.questions.map((q, i) => (
              <div key={q.qid} className="rud-card rounded-xl p-4" data-testid={`mock-q-${i}`}>
                <div className="text-sm font-medium mb-2">{i + 1}. {q.q}</div>
                <div className="space-y-1.5">
                  {q.options.map((opt, oi) => (
                    <button key={oi} data-testid={`mock-q-${i}-opt-${oi}`} onClick={() => setAnswers((a) => ({ ...a, [q.qid]: oi }))}
                      className={`w-full text-left text-sm rounded-lg px-3 py-2 border transition-colors ${answers[q.qid] === oi ? "bg-brand text-black border-brand" : "border-rud text-sec hover:border-brand/50"}`}>
                      {String.fromCharCode(65 + oi)}. {opt}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <button data-testid="mock-submit-btn" onClick={() => submit()} disabled={busy} className="btn-primary h-11 px-6 rounded-full text-sm mt-4 flex items-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />} Submit test
          </button>
        </div>
      )}

      {phase === "done" && result && (
        <div className="mt-3" data-testid="mock-result">
          <div className="flex flex-wrap items-center gap-4">
            <div className="text-center">
              <div className="font-serif text-5xl" style={{ color: result.passed ? "#22c55e" : "#f59e0b" }}>{result.score_pct}%</div>
              <div className="text-xs text-sec">{result.correct}/{result.total} correct · {result.passed ? "Passed" : "Keep practising"}</div>
            </div>
            <button data-testid="mock-retake-btn" onClick={() => setPhase("idle")} className="btn-outline h-10 px-5 rounded-full text-sm">Take another</button>
          </div>
          <div className="mt-4 space-y-3">
            {result.review.map((r, i) => (
              <div key={i} className="rud-card rounded-xl p-4" data-testid={`mock-review-${i}`}>
                <div className="text-sm font-medium flex items-start gap-2">
                  {r.correct ? <CheckCircle2 size={15} className="text-green-400 shrink-0 mt-0.5" /> : <XCircle size={15} className="text-red-400 shrink-0 mt-0.5" />}
                  {i + 1}. {r.q}
                </div>
                <div className="mt-2 text-xs space-y-1">
                  {r.options.map((opt, oi) => (
                    <div key={oi} className={`px-2 py-1 rounded ${oi === r.answer_index ? "text-green-400" : oi === r.your_index ? "text-red-400" : "text-sec"}`}>
                      {String.fromCharCode(65 + oi)}. {opt} {oi === r.answer_index ? " ✓" : oi === r.your_index && !r.correct ? " ✗ (your answer)" : ""}
                    </div>
                  ))}
                  {r.explanation && <div className="text-sec pt-1">💡 {r.explanation}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CourseView({ courseId, refCode, user, setUser, refreshUser, onBack }) {
  const { country } = useApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [openModule, setOpenModule] = useState(null);
  const [examOpen, setExamOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/lms/course/${courseId}`, { params: { country } }); setData(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [courseId, country]);
  useEffect(() => { load(); }, [load]);

  const enroll = async () => {
    if (!user) { toast.error("Sign in to enroll."); return; }
    setEnrolling(true);
    try {
      const { data: r } = await api.post("/lms/enroll", { course_id: courseId, ref_code: refCode || undefined, country });
      if (setUser && user) setUser({ ...user, credits: r.credits_remaining });
      toast.success(r.already ? "You're enrolled" : `Enrolled${r.discount_applied ? ` (saved ${r.discount_applied})` : ""}!`);
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setEnrolling(false); }
  };

  if (loading) return <div className="max-w-4xl mx-auto px-5 pt-32 grid place-items-center"><HeartLoader /></div>;
  if (!data?.course) return <div className="max-w-4xl mx-auto px-5 pt-32 text-center text-sec">Course not found. <button onClick={onBack} className="text-brand">Back</button></div>;
  const c = data.course;
  const enr = data.enrollment;
  const enrolled = enr?.paid;
  const modulesDone = c.syllabus.filter((m) => enr?.module_progress?.[String(m.module_no)]?.quiz_done).length;
  const pct = c.syllabus.length ? Math.round(modulesDone / c.syllabus.length * 100) : 0;
  const streak = (() => {
    const days = new Set(Object.values(enr?.module_progress || {}).map((p) => (p?.at || "").slice(0, 10)).filter(Boolean));
    if (!days.size) return 0;
    let n = 0; const d = new Date();
    for (;;) { const k = d.toISOString().slice(0, 10); if (days.has(k)) { n++; d.setDate(d.getDate() - 1); } else break; }
    return n;
  })();

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 pt-24 pb-16" data-testid="course-view">
      <button data-testid="course-back" onClick={onBack} className="flex items-center gap-1.5 text-sm text-sec hover:text-brand mb-5"><ArrowLeft size={15} /> All courses</button>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-1">{c.category} · {c.level}</div>
      <h1 className="font-serif text-4xl tracking-tighter">{c.title}</h1>
      {c.subtitle && <p className="text-sec mt-1">{c.subtitle}</p>}
      <p className="mt-3 leading-relaxed">{c.description}</p>
      <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-sec">
        <span className="flex items-center gap-1"><Clock size={14} /> {c.duration_hours}h</span>
        <span className="flex items-center gap-1"><BookOpen size={14} /> {c.module_count} modules</span>
        <span className="flex items-center gap-1"><Trophy size={14} /> Final exam: {c.exam_objective_count} MCQs + 1 essay</span>
        <span className="flex items-center gap-1"><Users size={14} /> {c.enroll_count} learners</span>
        <Stars value={c.rating_avg} count={c.rating_count} />
        <span className="flex items-center gap-1 text-brand"><Coins size={14} /> {c.price_credits} credits {c.base_credits > c.price_credits && <span className="text-sec line-through ml-1 text-xs opacity-60">{c.base_credits}</span>}</span>
      </div>

      <div className="rud-card rounded-2xl p-4 mt-5 flex flex-wrap items-center justify-between gap-3" data-testid="course-share-bar">
        <div className="text-sm text-sec flex items-center gap-2"><Share2 size={15} className="text-brand" /> Invite learners — share this course by link, WhatsApp, email or SMS</div>
        <ShareCourse courseId={courseId} title={c.title} refCode={enr?.share_code || ""} compact />
      </div>

      {c.exam_tag && <MockTest courseId={courseId} examTag={c.exam_tag} user={user} setUser={setUser} />}

      {c.learning_outcomes?.length > 0 && (
        <div className="rud-card rounded-2xl p-5 mt-6">
          <div className="text-xs uppercase tracking-widest text-brand mb-2">What you'll learn</div>
          <ul className="grid sm:grid-cols-2 gap-2">{c.learning_outcomes.map((o, i) => (
            <li key={i} className="flex items-start gap-2 text-sm"><CheckCircle2 size={15} className="text-brand shrink-0 mt-0.5" /> {o}</li>))}</ul>
        </div>
      )}

      {!enrolled && (
        <div className="rud-card rounded-2xl p-6 mt-6 flex flex-wrap items-center justify-between gap-4" data-testid="enroll-box">
          <div>
            <div className="font-medium">Enroll to unlock all modules &amp; the final exam</div>
            <div className="text-sm text-sec">{c.price_credits} credits · daily quizzes + assignments + VishwaGuru-University certificate on passing (≥35%)</div>
            {refCode && <div className="text-xs text-brand mt-1">Referral link applied — you get 6% off 🎉</div>}
          </div>
          <button data-testid="enroll-btn" onClick={enroll} disabled={enrolling} className="btn-primary h-11 px-6 flex items-center gap-2 disabled:opacity-60">
            {enrolling ? <Loader2 size={16} className="animate-spin" /> : <GraduationCap size={16} />} Enroll now
          </button>
        </div>
      )}

      <CourseMaterials courseId={courseId} isCreator={!!(user && c.creator_id === user.user_id)} enrolled={!!enrolled} user={user} setUser={setUser} />

      <div className="mt-8">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <h2 className="font-serif text-2xl">Syllabus</h2>
          {enrolled && (
            <div className="flex items-center gap-4" data-testid="course-progress">
              <span className="text-sm text-sec">{modulesDone}/{c.syllabus.length} modules</span>
              {streak > 0 && <span className="text-sm text-orange-400 flex items-center gap-1" data-testid="course-streak"><Flame size={14} /> {streak}-day streak</span>}
            </div>
          )}
        </div>
        {enrolled && (
          <div className="h-2 rounded-full bg-elevated border border-rud overflow-hidden mb-4" data-testid="course-progress-bar">
            <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.6 }} className="h-full bg-brand" />
          </div>
        )}
        <div className="space-y-2" data-testid="syllabus">
          {c.syllabus.map((m) => {
            const prog = enr?.module_progress?.[String(m.module_no)] || {};
            const done = prog.quiz_done;
            return (
              <div key={m.module_no} className="rud-card rounded-xl p-4 flex items-center justify-between gap-3" data-testid={`module-row-${m.module_no}`}>
                <div className="min-w-0">
                  <div className="font-medium flex items-center gap-2">{done && <CheckCircle2 size={15} className="text-brand" />}Module {m.module_no}: {m.title}</div>
                  <div className="text-sm text-sec line-clamp-1">{m.summary}</div>
                  {done && <div className="text-xs text-brand mt-0.5">Quiz {prog.quiz_score}%{prog.assignment ? ` · Assignment ${prog.assignment.score}%` : ""}</div>}
                </div>
                <button data-testid={`open-module-${m.module_no}`} disabled={!enrolled} onClick={() => setOpenModule(m.module_no)}
                  className={`shrink-0 text-sm h-9 px-4 rounded-full ${enrolled ? "btn-primary" : "btn-ghost opacity-60 cursor-not-allowed"}`}>{done ? "Review" : "Study"}</button>
              </div>
            );
          })}
        </div>
      </div>

      {enrolled && (() => {
        const modulesDone = c.syllabus.filter((m) => enr?.module_progress?.[String(m.module_no)]?.quiz_done).length;
        const examUnlocked = modulesDone >= c.syllabus.length && c.syllabus.length > 0;
        return (
        <div className="rud-card rounded-2xl p-6 mt-6 flex flex-wrap items-center justify-between gap-4" data-testid="exam-box">
          <div>
            <div className="font-medium flex items-center gap-2"><Trophy size={16} className="text-brand" /> Final Exam</div>
            <div className="text-sm text-sec">{c.exam_objective_count} objective questions + a ~500 word essay. Pause and resume anytime.</div>
            {!examUnlocked && !enr?.exam?.submitted && <div className="text-xs text-amber-400 mt-1">🔒 Finish all module quizzes to unlock ({modulesDone}/{c.syllabus.length} done)</div>}
            {enr?.exam?.submitted && <div className="text-xs text-brand mt-1">Completed — Final grade {enr.exam.grade} ({enr.exam.final_grade_pct}%)</div>}
          </div>
          <button data-testid="take-exam-btn" disabled={!examUnlocked && !enr?.exam?.submitted} onClick={() => setExamOpen(true)}
            className={`h-11 px-6 flex items-center gap-2 rounded-full ${(examUnlocked || enr?.exam?.submitted) ? "btn-primary" : "btn-ghost opacity-50 cursor-not-allowed"}`}>
            {enr?.exam?.submitted ? "Review exam" : enr?.exam?.started ? "Resume exam" : "Take exam"}
          </button>
        </div>
        );
      })()}

      {enrolled && <ReviewSection courseId={courseId} reviews={data.reviews || []} onDone={load} />}

      <AnimatePresence>
        {openModule != null && <ModuleModal courseId={courseId} moduleNo={openModule} onClose={() => { setOpenModule(null); load(); }} />}
        {examOpen && <ExamModal courseId={courseId} course={c} enrollment={enr} onClose={() => { setExamOpen(false); load(); }} setUser={setUser} user={user} refreshUser={refreshUser} />}
      </AnimatePresence>
    </div>
  );
}

function CourseMaterials({ courseId, isCreator, enrolled, user, setUser }) {
  const [mats, setMats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ kind: "session", title: "", url: "", recurrence: "once", when: "", price: 0 });
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/lms/courses/${courseId}/materials`); setMats(data.materials || []); }
    catch { /* silent */ }
    finally { setLoading(false); }
  }, [courseId]);
  useEffect(() => { load(); }, [load]);

  const KIND = {
    session: { icon: Radio, label: "Live Session", tint: "text-rose-400", post: 5 },
    video: { icon: PlayCircle, label: "Video", tint: "text-sky-400", post: 1 },
    pdf: { icon: FileText, label: "PDF", tint: "text-amber-400", post: 0 },
  };

  const access = async (m) => {
    if (!user) { toast.error("Sign in to access."); return; }
    setBusy(m.id);
    try {
      const { data } = await api.post(`/lms/materials/${m.id}/access`);
      if (data.charged > 0 && setUser && user) setUser({ ...user, credits: data.credits_remaining });
      if (data.charged > 0) toast.success(`Unlocked — ${data.charged} credits spent`);
      if (data.url) window.open(data.url, "_blank", "noopener");
      else toast.error("No link attached yet.");
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const post = async () => {
    if (!form.title.trim()) { toast.error("Add a title."); return; }
    if ((form.kind === "video" || form.kind === "pdf") && !form.url.trim()) { toast.error("Add the link/file URL."); return; }
    setPosting(true);
    try {
      let url = form.url.trim();
      if (form.kind === "session" && !url) url = `${window.location.origin}/meet?room=course-${courseId}`;
      const { data } = await api.post(`/lms/courses/${courseId}/materials`, {
        kind: form.kind, title: form.title.trim(), url,
        recurrence: form.recurrence, when: form.when.trim(), price: Number(form.price) || 0,
      });
      if (setUser && user) setUser({ ...user, credits: data.credits_remaining });
      toast.success(`Posted${KIND[form.kind].post ? ` · ${KIND[form.kind].post} credits fee` : ""}`);
      setForm({ kind: "session", title: "", url: "", recurrence: "once", when: "", price: 0 });
      setShowAdd(false);
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setPosting(false); }
  };

  const remove = async (m) => {
    setBusy(m.id);
    try { await api.delete(`/lms/materials/${m.id}`); toast.success("Removed"); await load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  if (loading) return null;
  if (!isCreator && mats.length === 0) return null;

  return (
    <div className="mt-8" data-testid="course-materials">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <h2 className="font-serif text-2xl flex items-center gap-2"><Video size={20} className="text-brand" /> Live Sessions &amp; Materials</h2>
          <p className="text-sm text-sec">Join live classes, watch videos and download printable PDFs from the course creator.</p>
        </div>
        {isCreator && (
          <button data-testid="materials-add-toggle" onClick={() => setShowAdd((s) => !s)} className="btn-primary h-10 px-4 flex items-center gap-2 shrink-0">
            <Plus size={16} /> Add material
          </button>
        )}
      </div>

      {isCreator && showAdd && (
        <div className="rud-card rounded-2xl p-5 mb-4 space-y-3" data-testid="materials-add-form">
          <div className="flex flex-wrap gap-2">
            {Object.entries(KIND).map(([k, v]) => (
              <button key={k} data-testid={`material-kind-${k}`} onClick={() => setForm((f) => ({ ...f, kind: k }))}
                className={`h-9 px-4 rounded-full text-sm flex items-center gap-1.5 ${form.kind === k ? "btn-primary" : "btn-ghost"}`}>
                <v.icon size={14} /> {v.label} {v.post > 0 && <span className="opacity-70 text-xs">· {v.post}cr to post</span>}
              </button>
            ))}
          </div>
          <input data-testid="material-title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            placeholder="Title (e.g. Weekly Doubt-Clearing Class)" className="w-full h-11 px-4 rounded-xl bg-elevated border border-rud outline-none focus:border-brand" />
          {form.kind === "session" ? (
            <>
              <input data-testid="material-url" value={form.url} onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                placeholder="Secure Meet / meeting link (optional — leave blank to use your Secure Meet room)" className="w-full h-11 px-4 rounded-xl bg-elevated border border-rud outline-none focus:border-brand" />
              <div className="flex flex-wrap gap-2">
                {["once", "daily", "weekly", "custom"].map((r) => (
                  <button key={r} onClick={() => setForm((f) => ({ ...f, recurrence: r }))}
                    className={`h-9 px-3 rounded-full text-sm capitalize ${form.recurrence === r ? "btn-primary" : "btn-ghost"}`}>{r}</button>
                ))}
              </div>
              <input data-testid="material-when" value={form.when} onChange={(e) => setForm((f) => ({ ...f, when: e.target.value }))}
                placeholder="When? (e.g. Every Sun 6 PM IST)" className="w-full h-11 px-4 rounded-xl bg-elevated border border-rud outline-none focus:border-brand" />
            </>
          ) : (
            <input data-testid="material-url" value={form.url} onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
              placeholder={form.kind === "video" ? "YouTube / video link" : "PDF file URL"} className="w-full h-11 px-4 rounded-xl bg-elevated border border-rud outline-none focus:border-brand" />
          )}
          {form.kind !== "video" && (
            <div className="flex items-center gap-2">
              <Coins size={15} className="text-brand" />
              <input data-testid="material-price" type="number" min="0" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                className="w-32 h-10 px-3 rounded-xl bg-elevated border border-rud outline-none focus:border-brand" />
              <span className="text-sm text-sec">credits for learners to {form.kind === "session" ? "join" : "download"} (0 = free) · you keep 94%</span>
            </div>
          )}
          <div className="flex items-center gap-2 pt-1">
            <button data-testid="material-post-btn" onClick={post} disabled={posting} className="btn-primary h-10 px-5 flex items-center gap-2 disabled:opacity-60">
              {posting ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Post
            </button>
            {KIND[form.kind].post > 0 && <span className="text-xs text-sec">Posting fee: {KIND[form.kind].post} credits</span>}
          </div>
        </div>
      )}

      <div className="space-y-2" data-testid="materials-list">
        {mats.length === 0 && <div className="text-sm text-sec rud-card rounded-xl p-4">No materials yet.</div>}
        {mats.map((m) => {
          const K = KIND[m.kind] || KIND.video;
          const free = m.kind === "video" || Number(m.price) <= 0;
          const canOpen = m.unlocked || free || m.is_creator;
          return (
            <div key={m.id} className="rud-card rounded-xl p-4 flex items-center justify-between gap-3" data-testid={`material-row-${m.id}`}>
              <div className="min-w-0">
                <div className="font-medium flex items-center gap-2"><K.icon size={16} className={K.tint} /> {m.title}</div>
                <div className="text-xs text-sec mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className={K.tint}>{K.label}</span>
                  {m.kind === "session" && m.when && <span className="flex items-center gap-1"><Calendar size={12} /> {m.when}{m.recurrence !== "once" ? ` · ${m.recurrence}` : ""}</span>}
                  {free ? <span className="text-brand">Free</span> : <span className="flex items-center gap-1 text-brand"><Coins size={12} /> {m.price} credits</span>}
                  <span>· by {m.creator_name}</span>
                  {m.access_count > 0 && <span>· {m.access_count} {m.kind === "session" ? "joined" : "opened"}</span>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button data-testid={`material-access-${m.id}`} onClick={() => access(m)} disabled={busy === m.id || (!enrolled && !m.is_creator && !free)}
                  className={`h-9 px-4 rounded-full text-sm flex items-center gap-1.5 ${(enrolled || m.is_creator || free) ? "btn-primary" : "btn-ghost opacity-60 cursor-not-allowed"}`}>
                  {busy === m.id ? <Loader2 size={14} className="animate-spin" /> : canOpen ? (m.kind === "session" ? <Video size={14} /> : m.kind === "pdf" ? <Download size={14} /> : <ExternalLink size={14} />) : <Lock size={14} />}
                  {m.kind === "session" ? "Join" : m.kind === "pdf" ? (canOpen ? "Download" : `Unlock`) : "Watch"}
                </button>
                {m.is_creator && (
                  <button data-testid={`material-delete-${m.id}`} onClick={() => remove(m)} disabled={busy === m.id} className="h-9 w-9 grid place-items-center rounded-full btn-ghost text-rose-400" title="Remove">
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ModuleModal({ courseId, moduleNo, onClose }) {
  const [m, setM] = useState(null);
  const [prog, setProg] = useState({});
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [quizRes, setQuizRes] = useState(null);
  const [assignment, setAssignment] = useState("");
  const [asgRes, setAsgRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [readDone, setReadDone] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/lms/course/${courseId}/module/${moduleNo}`);
        setM(data.module); setProg(data.progress || {});
        if (data.progress?.quiz_done) setReadDone(true);
        if (data.progress?.assignment?.text) { setAssignment(data.progress.assignment.text); setAsgRes(data.progress.assignment); }
      } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
      finally { setLoading(false); }
    })();
  }, [courseId, moduleNo]);

  const submitQuiz = async () => {
    if (Object.keys(answers).length < (m.quiz?.length || 0)) { toast.error("Answer all questions."); return; }
    setBusy(true);
    try {
      const arr = m.quiz.map((_, i) => (answers[i] != null ? answers[i] : -1));
      const { data } = await api.post("/lms/module/quiz", { course_id: courseId, module_no: moduleNo, answers: arr });
      setQuizRes(data); toast.success(`Quiz: ${data.score}%`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const submitAssignment = async () => {
    if (assignment.trim().length < 10) { toast.error("Write a bit more."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/lms/module/assignment", { course_id: courseId, module_no: moduleNo, text: assignment });
      setAsgRes(data); toast.success(`Assignment graded: ${data.score}%`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[60] bg-black/70 grid place-items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} className="rud-card rounded-2xl p-6 max-w-2xl w-full my-10" onClick={(e) => e.stopPropagation()} data-testid="module-modal">
        {loading ? <div className="grid place-items-center py-16"><HeartLoader /></div> : m && (
          <>
            <div className="flex items-center justify-between"><div className="text-xs uppercase tracking-widest text-brand">Module {moduleNo} of {m ? "" : ""}</div><button onClick={onClose} className="text-sec"><XCircle size={20} /></button></div>
            <h3 className="font-serif text-2xl mt-1">{m.title}</h3>
            <div className="text-sm leading-relaxed whitespace-pre-wrap text-sec mt-3">{m.reading}</div>
            {m.key_points?.length > 0 && <ul className="mt-3 space-y-1">{m.key_points.map((k, i) => <li key={i} className="flex items-start gap-2 text-sm"><span className="text-brand">•</span> {k}</li>)}</ul>}

            <div className="mt-6 pt-5 border-t border-rud">
              <div className="font-medium mb-3 flex items-center gap-2"><Trophy size={15} className="text-brand" /> Lesson quiz ({m.quiz.length} questions)</div>
              {!readDone ? (
                <div className="rud-card rounded-lg p-4 text-center">
                  <p className="text-sm text-sec mb-3">🔒 Read the lesson above first — then the quiz unlocks.</p>
                  <button data-testid="mod-mark-read" onClick={() => setReadDone(true)} className="btn-primary h-9 px-4 text-sm">I've read this — start the quiz</button>
                </div>
              ) : (<>
              {m.quiz.map((qq, i) => (
                <div key={i} className="mb-3">
                  <div className="text-sm font-medium mb-2">{i + 1}. {qq.q}</div>
                  <div className="space-y-1.5">{qq.options.map((op, j) => {
                    const showCorrect = quizRes && quizRes.results[i].correct === j;
                    const showWrong = quizRes && answers[i] === j && quizRes.results[i].correct !== j;
                    return (
                      <label key={j} data-testid={`mod-quiz-${i}-opt-${j}`} onClick={() => !quizRes && setAnswers({ ...answers, [i]: j })}
                        className={`flex items-center gap-2 rounded-lg border p-2 text-sm cursor-pointer ${showCorrect ? "border-emerald-500 bg-emerald-500/10" : showWrong ? "border-rose-500 bg-rose-500/10" : answers[i] === j ? "border-brand bg-brand/5" : "border-rud"}`}>
                        <span className={`w-3.5 h-3.5 rounded-full border shrink-0 ${answers[i] === j ? "bg-brand border-brand" : "border-sec"}`} /> {op}
                      </label>);
                  })}</div>
                </div>
              ))}
              {!quizRes ? <button data-testid="mod-quiz-submit" onClick={submitQuiz} disabled={busy} className="btn-primary h-9 px-4 text-sm mt-1">Submit quiz</button>
                : <div className="text-sm text-brand">Score {quizRes.score}% ({quizRes.correct}/{quizRes.total})</div>}
              </>)}
            </div>

            <div className="mt-6 pt-5 border-t border-rud">
              <div className="font-medium mb-2 flex items-center gap-2"><FileText size={15} className="text-brand" /> Assignment</div>
              <p className="text-sm text-sec mb-2">{m.assignment.prompt}</p>
              <textarea data-testid="mod-assignment-text" value={assignment} onChange={(e) => setAssignment(e.target.value)} rows={5} placeholder="Write your response…" className="rud-input w-full rounded-lg px-3 py-2 text-sm resize-none" />
              <button data-testid="mod-assignment-submit" onClick={submitAssignment} disabled={busy} className="btn-primary h-9 px-4 text-sm mt-2 flex items-center gap-2">{busy ? <Loader2 size={14} className="animate-spin" /> : null} Submit for AI grading</button>
              {asgRes && <div className="mt-3 rounded-lg bg-brand/10 border border-brand/30 p-3 text-sm"><b className="text-brand">Score {asgRes.score}%</b> — {asgRes.feedback}</div>}
            </div>
            <button onClick={onClose} className="btn-ghost rounded-full w-full h-10 mt-5">Done</button>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}

function ExamModal({ courseId, course, enrollment, onClose, setUser, user, refreshUser }) {
  const [exam, setExam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [essay, setEssay] = useState("");
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const saveTimer = useRef(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/lms/exam/${courseId}`);
        setExam(data);
        setAnswers(data.saved?.answers || {});
        setEssay(data.saved?.essay || "");
        if (data.result) setResult({ ...data.result, certificate: null });
      } catch (e) { toast.error(apiError(e.response?.data?.detail)); onClose(); }
      finally { setLoading(false); }
    })();
  }, [courseId]);

  const autosave = (ans, ess) => {
    if (exam?.saved?.submitted) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.post("/lms/exam/save", { course_id: courseId, answers: ans, essay: ess }).catch(() => {});
    }, 800);
  };
  const pick = (qi, oi) => { const a = { ...answers, [qi]: oi }; setAnswers(a); autosave(a, essay); };
  const onEssay = (v) => { setEssay(v); autosave(answers, v); };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/lms/exam/submit", { course_id: courseId, answers, essay });
      setResult(data);
      if (refreshUser) refreshUser();
      toast.success(data.passed ? `Passed! Grade ${data.grade}` : `Scored ${data.final_grade_pct}%`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const submitted = result || exam?.saved?.submitted;
  const answeredCount = Object.keys(answers).length;
  const essayWords = essay.trim() ? essay.trim().split(/\s+/).length : 0;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[60] bg-black/80 grid place-items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <motion.div initial={{ scale: 0.96, y: 20 }} animate={{ scale: 1, y: 0 }} className="rud-card rounded-2xl p-6 max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="exam-modal">
        {loading ? <div className="grid place-items-center py-16 text-sec"><div className="text-center"><HeartLoader /><p className="text-sm">Preparing your exam…</p></div></div>
          : result && result.final_grade_pct != null ? (
            <ExamResult result={result} course={course} enrollment={enrollment} onClose={onClose} />
          ) : exam?.saved?.submitted && exam?.result ? (
            <ExamResult result={exam.result} course={course} enrollment={enrollment} onClose={onClose} />
          ) : (
            <>
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-serif text-2xl flex items-center gap-2"><Trophy className="text-brand" /> Final Exam</h3>
                <button onClick={onClose} className="text-sec text-sm flex items-center gap-1"><Clock size={13} /> Save &amp; exit</button>
              </div>
              <p className="text-sec text-sm mb-4">Answer the questions and write your essay. Progress saves automatically — leave and resume anytime.</p>
              <div className="text-xs text-sec mb-4">{answeredCount}/{exam.objective_count} answered · essay {essayWords} words</div>
              <div className="space-y-4">
                {exam.objective.map((qq, i) => (
                  <div key={i} className="rud-card rounded-xl p-3">
                    <div className="text-sm font-medium mb-2">{i + 1}. {qq.q}</div>
                    <div className="space-y-1.5">{qq.options.map((op, j) => (
                      <label key={j} data-testid={`exam-q${i}-opt-${j}`} onClick={() => pick(String(i), j)}
                        className={`flex items-center gap-2 rounded-lg border p-2 text-sm cursor-pointer ${answers[String(i)] === j ? "border-brand bg-brand/5" : "border-rud hover:border-brand/50"}`}>
                        <span className={`w-3.5 h-3.5 rounded-full border shrink-0 ${answers[String(i)] === j ? "bg-brand border-brand" : "border-sec"}`} /> {op}
                      </label>))}</div>
                  </div>
                ))}
              </div>
              <div className="mt-5">
                <div className="font-medium text-sm mb-1">Essay (~{exam.essay.min_words} words)</div>
                <p className="text-sm text-sec mb-2">{exam.essay.prompt}</p>
                <textarea data-testid="exam-essay" value={essay} onChange={(e) => onEssay(e.target.value)} rows={8} placeholder="Write your essay here…" className="rud-input w-full rounded-lg px-3 py-2 text-sm resize-none" />
                <div className="text-xs text-sec mt-1">{essayWords} words</div>
              </div>
              <div className="flex gap-2 mt-5">
                <button data-testid="exam-submit" onClick={submit} disabled={submitting} className="btn-primary h-11 px-6 flex items-center gap-2 disabled:opacity-60">{submitting ? <><Loader2 size={16} className="animate-spin" /> Grading…</> : <><CheckCircle2 size={16} /> Submit exam</>}</button>
                <button onClick={onClose} className="btn-ghost rounded-full h-11 px-5">Save &amp; finish later</button>
              </div>
            </>
          )}
      </motion.div>
    </motion.div>
  );
}

function GuruDakshina({ courseId, options }) {
  const [given, setGiven] = useState(false);
  const [busy, setBusy] = useState(0);
  const give = async (amt) => {
    setBusy(amt);
    try { const { data } = await api.post("/lms/dakshina", { course_id: courseId, amount: amt }); toast.success(data.message); setGiven(true); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(0); }
  };
  if (given) return <div className="mt-5 rud-card rounded-xl p-4 text-center text-sm text-brand" data-testid="dakshina-done">🙏 Thank you — your Guru Dakshina has been offered.</div>;
  return (
    <div className="mt-5 rud-card rounded-xl p-4" data-testid="guru-dakshina">
      <div className="text-sm font-medium">🙏 Guru Dakshina <span className="text-xs text-sec font-normal">· optional, never required</span></div>
      <p className="text-xs text-sec mt-1">This course is free. If it helped you, offer a heartfelt Guru Dakshina — <b className="text-brand">30% goes to your teacher</b>, the rest sustains free learning for all.</p>
      <div className="flex flex-wrap gap-2 mt-3" data-testid="dakshina-options">
        {options.map((a) => (
          <button key={a} data-testid={`dakshina-${a}`} disabled={!!busy} onClick={() => give(a)}
            className="btn-ghost rounded-full h-9 px-4 text-sm disabled:opacity-50">{busy === a ? "…" : a}</button>
        ))}
      </div>
    </div>
  );
}

function ExamResult({ result, course, enrollment, onClose }) {
  const passed = result.passed;
  const cert = result.certificate;
  return (
    <div data-testid="exam-result">
      <div className="text-center">
        {passed ? <Award size={48} className="mx-auto text-brand" /> : <XCircle size={48} className="mx-auto text-rose-400" />}
        <div className="font-serif text-4xl mt-3">{result.final_grade_pct}%</div>
        <div className={`text-sm mt-1 ${passed ? "text-brand" : "text-rose-400"}`}>Final grade {result.grade}</div>
      </div>
      <div className="grid grid-cols-3 gap-2 mt-4 text-center text-sm">
        <div className="rud-card rounded-lg p-3"><div className="text-lg font-medium">{result.objective_pct}%</div><div className="text-xs text-sec">Objective (70%)</div></div>
        <div className="rud-card rounded-lg p-3"><div className="text-lg font-medium">{result.essay_score}%</div><div className="text-xs text-sec">Essay (20%)</div></div>
        <div className="rud-card rounded-lg p-3"><div className="text-lg font-medium">{result.coursework_pct}%</div><div className="text-xs text-sec">Coursework (10%)</div></div>
      </div>
      {result.essay_feedback && <div className="mt-3 rounded-lg bg-elevated border border-rud p-3 text-sm"><b className="text-brand">Essay feedback:</b> {result.essay_feedback}</div>}
      {passed && cert && <CertificateCard cert={cert} enrollment={enrollment} course={course} />}
      {passed && !cert && <p className="text-center text-sm text-brand mt-4">You passed! See your certificate under “My learning”.</p>}
      {passed && result.guru_dakshina?.eligible && !result.guru_dakshina?.given && (
        <GuruDakshina courseId={enrollment?.course_id || course?.id} options={result.guru_dakshina.options} />
      )}
      {!passed && <p className="text-center text-sm text-sec mt-4">You need {result.pass_mark}% to earn a certificate. Review the modules and try the exam again.</p>}
      <button data-testid="exam-result-close" onClick={onClose} className="btn-ghost rounded-full w-full h-10 mt-5">Close</button>
    </div>
  );
}

function ReviewSection({ courseId, reviews, onDone }) {
  const [rating, setRating] = useState(0);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!rating) { toast.error("Pick a star rating."); return; }
    setBusy(true);
    try { await api.post("/lms/review", { course_id: courseId, rating, text }); toast.success("Thanks for your review!"); setText(""); onDone(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-8" data-testid="review-section">
      <h2 className="font-serif text-2xl mb-3">Reviews</h2>
      <div className="rud-card rounded-xl p-4 mb-4">
        <div className="flex items-center gap-1 mb-2">{[1, 2, 3, 4, 5].map((s) => (
          <button key={s} data-testid={`review-star-${s}`} onClick={() => setRating(s)}><Star size={22} className={s <= rating ? "fill-amber-400 text-amber-400" : "text-sec"} /></button>))}</div>
        <textarea data-testid="review-text" value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Share your thoughts (optional)…" className="rud-input w-full rounded-lg px-3 py-2 text-sm resize-none" />
        <button data-testid="review-submit" onClick={submit} disabled={busy} className="btn-primary h-9 px-4 text-sm mt-2">Post review</button>
      </div>
      <div className="space-y-2">{reviews.map((r, i) => (
        <div key={i} className="rud-card rounded-xl p-3">
          <div className="flex items-center gap-2"><span className="font-medium text-sm">{r.user_name}</span><span className="flex text-amber-400">{Array.from({ length: r.rating }).map((_, j) => <Star key={j} size={12} className="fill-amber-400" />)}</span></div>
          {r.text && <div className="text-sm text-sec mt-1">{r.text}</div>}
        </div>))}
        {reviews.length === 0 && <p className="text-sec text-sm">No reviews yet — be the first!</p>}
      </div>
    </div>
  );
}

function CertificateCard({ cert, enrollment, course }) {
  const origin = window.location.origin;
  const verifyLink = `${origin}/learn?verify=${cert.code}`;
  const shareLink = enrollment?.share_code ? `${origin}/learn?course=${course.id}&ref=${enrollment.share_code}` : `${origin}/learn?course=${course?.id || ""}`;
  const pdfUrl = `${API}/lms/certificate/${cert.code}/pdf`;
  const shareText = `🎓 I earned a VishwaGuru-University certificate in "${cert.course_title}" (Grade ${cert.grade}, ${cert.score}%)! Take the course: ${shareLink}`;
  const social = (net) => {
    const enc = encodeURIComponent(shareText);
    const links = { whatsapp: `https://wa.me/?text=${enc}`, x: `https://twitter.com/intent/tweet?text=${enc}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareLink)}`, telegram: `https://t.me/share/url?url=${encodeURIComponent(shareLink)}&text=${enc}` };
    if (net === "copy") { navigator.clipboard.writeText(shareText).then(() => toast("Copied — includes your referral link")).catch(() => {}); return; }
    window.open(links[net], "_blank", "noopener,noreferrer");
  };
  return (
    <div className="mt-5" data-testid="certificate-card">
      <div className="rounded-xl border-2 border-[#d4af37] bg-[#0b0b0e] text-white p-5 text-center">
        <div className="text-[#d4af37] font-serif text-lg">VishwaGuru-University</div>
        <div className="text-[10px] text-white/50">A decentralised, democratic distance-learning institution</div>
        <div className="text-xs text-white/60 mt-2">Certificate of Completion</div>
        <div className="font-serif text-2xl mt-2">{cert.user_name}</div>
        <div className="text-xs text-white/60 mt-2">has completed</div>
        <div className="font-medium mt-1">{cert.course_title}</div>
        <div className="text-[#d4af37] text-sm mt-2 font-medium">Final Grade {cert.grade} · {cert.score}%</div>
        <div className="text-[10px] text-white/50 mt-3">Verify code: {cert.code}</div>
      </div>
      <p className="text-[11px] text-sec text-center mt-2">A copy has been emailed to you and saved to your profile.</p>
      <div className="mt-3">
        <div className="text-xs uppercase tracking-widest text-sec mb-2 flex items-center gap-1.5"><Share2 size={13} /> Share (your referral link earns you credits)</div>
        <div className="flex flex-wrap gap-2">
          <a data-testid="cert-download" href={pdfUrl} target="_blank" rel="noreferrer" className="btn-primary text-xs h-9 px-3 flex items-center gap-1.5"><Download size={13} /> Download PDF</a>
          <button data-testid="cert-share-whatsapp" onClick={() => social("whatsapp")} className="btn-ghost rounded-full text-xs h-9 px-3">WhatsApp</button>
          <button data-testid="cert-share-x" onClick={() => social("x")} className="btn-ghost rounded-full text-xs h-9 px-3">X</button>
          <button data-testid="cert-share-linkedin" onClick={() => social("linkedin")} className="btn-ghost rounded-full text-xs h-9 px-3">LinkedIn</button>
          <button data-testid="cert-share-copy" onClick={() => social("copy")} className="btn-ghost rounded-full text-xs h-9 px-3 flex items-center gap-1"><Copy size={12} /> Copy</button>
        </div>
      </div>
    </div>
  );
}

function MyLearning({ user, onOpen, onVerify }) {
  const [data, setData] = useState(null);
  const [created, setCreated] = useState([]);
  const [teacherEarned, setTeacherEarned] = useState(0);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!user) { setLoading(false); return; }
    Promise.all([
      api.get("/lms/my").then(({ data }) => setData(data)).catch(() => {}),
      api.get("/lms/created").then(({ data }) => { setCreated(data.courses || []); setTeacherEarned(data.teacher_earned_total || 0); }).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [user]);
  if (!user) return <div className="rud-card rounded-2xl p-6 text-center text-sec text-sm">Sign in to see your courses, exams and certificates.</div>;
  if (loading) return <div className="grid place-items-center py-16"><HeartLoader /></div>;
  const enrollments = data?.enrollments || [];
  const certs = data?.certificates || [];
  const refs = data?.referral_rewards || [];
  return (
    <div className="space-y-8" data-testid="my-learning">
      <div className="grid sm:grid-cols-3 gap-3" data-testid="referral-dashboard">
        <div className="rud-card rounded-2xl p-5"><div className="text-sm text-sec">Referral credits earned</div><div className="font-serif text-3xl text-brand" data-testid="ref-credits">{data?.referral_credits_earned || 0}</div></div>
        <div className="rud-card rounded-2xl p-5"><div className="text-sm text-sec">People referred</div><div className="font-serif text-3xl">{data?.referral_count || 0}</div></div>
        <div className="rud-card rounded-2xl p-5"><div className="text-sm text-sec">Certificates earned</div><div className="font-serif text-3xl">{certs.length}</div></div>
      </div>

      {created.length > 0 && (
        <div data-testid="created-courses">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h3 className="font-serif text-2xl">Courses you launched</h3>
            <div className="flex items-center gap-2 text-sm rud-card rounded-full px-4 py-1.5" data-testid="teacher-earnings-total">
              <Coins size={15} className="text-brand" /> Teacher payouts earned: <b className="text-brand">{teacherEarned}</b> credits
            </div>
          </div>
          <div className="space-y-2">{created.map((c) => (
            <div key={c.id} className="rud-card rounded-xl p-4" data-testid={`created-course-${c.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium flex items-center gap-2">{c.exam_tag ? <Landmark size={14} className="text-brand" /> : null}{c.title}</div>
                  <div className="text-xs text-sec">{c.category} · {c.level} · {c.duration_hours}h · {c.enroll_count} learners{c.teacher_earned > 0 ? ` · earned ${c.teacher_earned} cr` : ""}</div>
                </div>
                <button data-testid={`open-created-${c.id}`} onClick={() => onOpen(c.id)} className="btn-primary text-xs h-9 px-3">Open</button>
              </div>
              <div className="mt-3 pt-3 border-t border-rud">
                <div className="text-[11px] uppercase tracking-widest text-sec mb-2">Share &amp; invite learners · you earn 15% credits per enrollment</div>
                <ShareCourse courseId={c.id} title={c.title} compact />
              </div>
            </div>))}</div>
        </div>
      )}

      {refs.length > 0 && (
        <div>
          <h3 className="font-serif text-2xl mb-3">Your referrals</h3>
          <div className="space-y-2">{refs.map((r, i) => (
            <div key={i} className="rud-card rounded-xl p-3 flex items-center justify-between text-sm">
              <span><b>{r.referred_name}</b> took <b>{r.course_title}</b></span>
              <span className="text-brand flex items-center gap-1"><Coins size={13} /> +{r.reward_credits}</span>
            </div>))}</div>
        </div>
      )}

      <div>
        <h3 className="font-serif text-2xl mb-3">My courses</h3>
        {enrollments.length === 0 ? <p className="text-sec text-sm">No courses yet. Explore or create one!</p> : (
          <div className="space-y-2">{enrollments.map((e) => (
            <div key={e.id} className="rud-card rounded-xl p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`my-enrollment-${e.course_id}`}>
              <div>
                <div className="font-medium">{e.course_title}</div>
                <div className="text-xs text-sec">{e.category} · {e.modules_done}/{e.module_count} modules · {e.exam.submitted ? `Exam ${e.exam.grade} (${e.exam.final_grade_pct}%)` : e.exam.started ? "Exam in progress" : "Exam not started"}</div>
              </div>
              <div className="flex gap-2">
                {e.certificate_code && <button onClick={() => onVerify(e.certificate_code)} className="btn-ghost rounded-full text-xs h-9 px-3 flex items-center gap-1"><Award size={13} /> Certificate</button>}
                <button data-testid={`resume-${e.course_id}`} onClick={() => onOpen(e.course_id)} className="btn-primary text-xs h-9 px-3">Open</button>
              </div>
            </div>))}</div>
        )}
      </div>

      {certs.length > 0 && (
        <div>
          <h3 className="font-serif text-2xl mb-3">Certificates</h3>
          <div className="grid sm:grid-cols-2 gap-3">{certs.map((c) => (
            <div key={c.code} className="rounded-xl border-2 border-[#d4af37] bg-[#0b0b0e] text-white p-4" data-testid={`cert-${c.code}`}>
              <div className="text-[#d4af37] font-serif">VishwaGuru-University</div>
              <div className="font-medium mt-2">{c.course_title}</div>
              <div className="text-sm text-white/70">Grade {c.grade} · {c.score}%</div>
              <div className="flex gap-2 mt-3">
                <a href={`${API}/lms/certificate/${c.code}/pdf`} target="_blank" rel="noreferrer" className="btn-primary text-xs h-8 px-3 flex items-center gap-1"><Download size={12} /> PDF</a>
                <button onClick={() => onVerify(c.code)} className="btn-ghost rounded-full text-xs h-8 px-3">Verify</button>
              </div>
            </div>))}</div>
        </div>
      )}
    </div>
  );
}

function Leaderboard() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/lms/leaderboard").then(({ data }) => setRows(data.leaders)).catch(() => setRows([])); }, []);
  if (!rows) return <div className="grid place-items-center py-16"><HeartLoader /></div>;
  const medal = ["text-amber-400", "text-slate-300", "text-orange-400"];
  return (
    <div data-testid="leaderboard">
      <div className="flex items-center gap-2 mb-4"><Medal className="text-brand" /><h3 className="font-serif text-2xl">Top learners at VishwaGuru-University</h3></div>
      {rows.length === 0 ? <p className="text-sec text-sm">No certificates awarded yet. Be the first on the board!</p> : (
        <div className="space-y-2">{rows.map((r) => (
          <div key={r.rank} className="rud-card rounded-xl p-4 flex items-center gap-4" data-testid={`leader-${r.rank}`}>
            <div className={`font-serif text-2xl w-8 text-center ${medal[r.rank - 1] || "text-sec"}`}>{r.rank}</div>
            <div className="flex-1"><div className="font-medium">{r.name}</div><div className="text-xs text-sec">{r.certificates} certificate(s) · avg {r.avg_score}%</div></div>
            <div className="text-brand font-medium flex items-center gap-1"><Trophy size={15} /> {r.best}%</div>
          </div>))}</div>
      )}
    </div>
  );
}

function VerifyView({ code, onBack }) {
  const [state, setState] = useState({ loading: true });
  useEffect(() => {
    api.get(`/lms/certificate/${code}`).then(({ data }) => setState({ loading: false, cert: data.certificate })).catch(() => setState({ loading: false, invalid: true }));
  }, [code]);
  return (
    <div className="max-w-lg mx-auto px-5 pt-28 pb-16 text-center" data-testid="verify-view">
      <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-sec hover:text-brand mb-6 mx-auto"><ArrowLeft size={15} /> Back to Learn</button>
      {state.loading ? <HeartLoader /> : state.invalid ? (
        <div className="rud-card rounded-2xl p-8"><XCircle size={40} className="mx-auto text-rose-400" /><p className="mt-3 font-medium">Certificate not found</p></div>
      ) : (
        <div className="rounded-2xl border-2 border-[#d4af37] bg-[#0b0b0e] text-white p-8">
          <CheckCircle2 size={36} className="mx-auto text-brand" />
          <div className="text-[#d4af37] font-serif text-xl mt-3">Verified · VishwaGuru-University</div>
          <div className="text-[10px] text-white/50">A decentralised, democratic distance-learning institution</div>
          <div className="font-serif text-3xl mt-4">{state.cert.user_name}</div>
          <div className="text-white/60 text-sm mt-2">completed</div>
          <div className="font-medium mt-1">{state.cert.course_title}</div>
          <div className="text-[#d4af37] mt-2">Grade {state.cert.grade} · {state.cert.score}%</div>
          <div className="text-xs text-white/50 mt-4">Code {state.cert.code} · Issued {(state.cert.issued_at || "").slice(0, 10)}</div>
        </div>
      )}
    </div>
  );
}
